import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';

// UTILIDADES DE FIREBASE Y NEGOCIO CONSOLIDADAS
import { 
    deletePolizaFull, 
    deleteContractFull,
    rehabilitarPolizaFull, 
    rehabilitarContratoFull 
} from '../../../firebase/firebaseUtils';

// COMPONENTES REUTILIZABLES DE INTERFAZ
import PageHeader from '../../cpanel/PageHeader';   
import FilterBar from './FilterBar';

// COMPONENTES UI PÓLIZA
import CardPoliza from '../../ui/CardPoliza';
import AuthModal from '../../ui/AuthModal';
import ModalEmision from '../../ui/ModalEmision';
import ModalDetallePoliza from '../../ui/ModalDetallePoliza';
import ModalAnulacion from '../../ui/ModalAnulacion';

// COMPONENTE COMÚN DE BÚSQUEDA
import BuscadorInteligente from '../../ui/BuscadorInteligente';

// COMPONENTES UI CONTRATO (ART)
import CardContrato from '../../ui/CardContrato';
import ModalEdicionContrato from '../../ui/ModalEdicionContrato';
import ModalAnulacionContratos from '../../ui/ModalAnulacionContratos';
import ModalDetalleContrato from '../../ui/ModalDetalleContrato';

// PERMISOS CENTRALIZADOS (ACL)
import { usePermissions } from '../../hooks/usePermissions';

/**
 * Helper síncrono para calcular el estado real en memoria basándose en las fechas.
 */
const getPolizaStatusInRealtime = (p) => {
    if (!p) return 'PENDIENTE';
    const estadoOriginal = (p.type === 'contrato' ? p.estadoPoliza : p.estadoEmision || '').toUpperCase();
    
    if (estadoOriginal === 'ANULADA') return 'ANULADA';

    const noCalcular = [
        'PENDIENTE DE EMISIÓN',
        'PENDIENTE DE INSPECCIÓN',
        'PROPUESTA INGRESADA'
    ].includes(estadoOriginal);

    if (noCalcular) return estadoOriginal;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = p.inicioVigencia ? new Date(p.inicioVigencia + "T00:00:00") : null;
    const end = p.finVigencia ? new Date(p.finVigencia + "T00:00:00") : null;

    if (end && today > end) return 'VENCIDA';
    if (start && today < start) return 'NO INICIADA';
    if (start || end) return 'VIGENTE';

    return estadoOriginal || 'PENDIENTE';
};

const BuscadorPolizasPage = () => {
    const navigate = useNavigate();
    
    // --- ESTADOS DE BÚSQUEDA ---
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [searched, setSearched] = useState(false);
    
    // Consumimos el Hook de Permisos unificado
    const { isFullUser, loadingAuth } = usePermissions();

    // --- ESTADOS DE FILTROS ---
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        estado: 'VIGENTE', 
        motivo: '',
        riesgo: '',
        compania: '',
        patente: '' 
    });

    // --- ESTADOS MODALES PÓLIZA ---
    const [showEmissionModal, setShowEmissionModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showAnulacionModal, setShowAnulacionModal] = useState(false);
    const [editingPoliza, setEditingPoliza] = useState(null);
    const [viewingPoliza, setViewingPoliza] = useState(null);
    const [polizaToAnular, setPolizaToAnular] = useState(null);

    // --- ESTADOS MODALES CONTRATO (ART) ---
    const [showEditContratoModal, setShowEditContratoModal] = useState(false);
    const [showAnulacionContratoModal, setShowAnulacionContratoModal] = useState(false);
    const [showDetailContratoModal, setShowDetailContratoModal] = useState(false);
    const [editingContrato, setEditingContrato] = useState(null);
    const [contratoToAnular, setContratoToAnular] = useState(null);
    const [viewingContrato, setViewingContrato] = useState(null);

    // --- AUTH MODAL DE SEGURIDAD ---
    const [authModalConfig, setAuthModalConfig] = useState({ open: false, type: '', item: null });

    // --- HANDLERS PÓLIZA ---
    const handleOpenEmission = (p) => { setEditingPoliza(p); setShowEmissionModal(true); };
    const handleViewDetailPoliza = (p) => { setViewingPoliza(p); setShowDetailModal(true); };
    const handleOpenAnulacionPoliza = (p) => { setPolizaToAnular(p); setShowAnulacionModal(true); };
    
    // --- HANDLERS CONTRATO ---
    const handleOpenEditContrato = (c) => { setEditingContrato(c); setShowEditContratoModal(true); };
    const handleViewDetailContrato = (c) => { setViewingContrato(c); setShowDetailContratoModal(true); };
    const handleOpenAnulacionContrato = (c) => { setContratoToAnular(c); setShowAnulacionContratoModal(true); };

    // --- ACCIONES CON AUTORIZACIÓN (PIN) ---
    const handleDeleteClick = (item) => {
        setAuthModalConfig({ open: true, type: 'DELETE', item });
    };

    const handleRehabilitarClick = (item) => {
        setAuthModalConfig({ open: true, type: 'REHABILITAR', item });
    };

    const handleConfirmAuthAction = async () => {
        const { item, type } = authModalConfig;
        if (!item) return;

        try {
            if (type === 'DELETE') {
                if (item.type === 'contrato') {
                    await deleteContractFull(item.clienteId, item.id);
                } else {
                    await deletePolizaFull(item.clienteId, item.id, item.nPoliza || null);
                }
                setResults(prev => prev.filter(p => p.id !== item.id));
            } 
            else if (type === 'REHABILITAR') {
                if (item.type === 'contrato') {
                    await rehabilitarContratoFull(item.clienteId, item.id);
                    setResults(prev => prev.map(p => p.id === item.id ? { ...p, estadoPoliza: 'VIGENTE' } : p));
                } else {
                    await rehabilitarPolizaFull(item.clienteId, item.id);
                    setResults(prev => prev.map(p => p.id === item.id ? { ...p, estadoEmision: 'VIGENTE' } : p));
                }
            }
        } catch (error) {
            console.error(`Error ejecutando acción de ${type}:`, error);
        } finally {
            setAuthModalConfig({ open: false, type: '', item: null });
        }
    };

    // --- MANEJO DE RESULTADOS DE BÚSQUEDA ---
    const handleSearchResult = async (data) => {
        setSyncing(true);
        setSearched(true);
        
        const validData = data.filter(item => 
            (item.compania && item.compania !== '---') || 
            item.nPoliza || 
            item.nContrato || 
            item.patente ||
            (Array.isArray(item.patentes) && item.patentes.length > 0)
        ).map(item => {
            const isContrato = item.type === 'contrato' || !!item.nContrato || item.riesgo?.toUpperCase() === 'ART';
            return {
                ...item,
                type: isContrato ? 'contrato' : (item.type || 'poliza')
            };
        });

        const synchronizedData = await syncStatusWithDB(validData);
        setResults(synchronizedData);
        setSyncing(false);
    };

    const syncStatusWithDB = async (list) => {
        const syncPromises = list.map(async (item) => {
            const currentStatus = (item.type === 'contrato' ? item.estadoPoliza : item.estadoEmision || '').toUpperCase();
            
            const noCalcular = [
                'PENDIENTE DE EMISIÓN',
                'PENDIENTE DE INSPECCIÓN',
                'PROPUESTA INGRESADA',
                'ANULADA'
            ].includes(currentStatus);

            const realStatus = noCalcular 
                ? currentStatus 
                : getPolizaStatusInRealtime(item);

            if (realStatus !== currentStatus) {
                try {
                    const collectionName = item.type === 'contrato' ? 'contratos' : 'polizas';
                    const ref = doc(db, `public/data/clientes/${item.clienteId}/${collectionName}`, item.id);
                    
                    const updatePayload = item.type === 'contrato' 
                        ? { estadoPoliza: realStatus } 
                        : { estadoEmision: realStatus };

                    await updateDoc(ref, updatePayload);
                    
                    if (item.type === 'contrato') item.estadoPoliza = realStatus;
                    else item.estadoEmision = realStatus;
                    
                } catch (e) { 
                    console.error(`Error sincronizando estado de ${item.id}:`, e); 
                }
            }
            return item;
        });

        return await Promise.all(syncPromises);
    };

    // --- FILTRADO INTELIGENTE EN MEMORIA ---
    const filteredResults = useMemo(() => {
        return results.filter(item => {
            const itemEstado = getPolizaStatusInRealtime(item);
            const itemMotivo = item.motivo || item.tipoOperacion;
            const itemRiesgo = item.riesgo || (item.type === 'contrato' ? 'ART' : '');

            const matchEstado = filters.estado ? itemEstado === filters.estado.toUpperCase() : true;
            const matchCompania = filters.compania ? item.compania === filters.compania : true;
            const matchRiesgo = filters.riesgo ? itemRiesgo === filters.riesgo : true;
            const matchMotivo = filters.motivo ? itemMotivo === filters.motivo : true;
            
            let matchPatente = true;
            if (filters.patente) {
                const searchVal = filters.patente.toUpperCase().replace(/[-\s]/g, '');
                const inOldField = (item.patente || '').toUpperCase().replace(/[-\s]/g, '').includes(searchVal);
                const inNewArray = Array.isArray(item.patentes) && item.patentes.some(p => 
                    (p || '').toUpperCase().replace(/[-\s]/g, '').includes(searchVal)
                );
                matchPatente = inOldField || inNewArray;
            }

            return matchEstado && matchCompania && matchRiesgo && matchMotivo && matchPatente;
        });
    }, [results, filters]);

    // HANDLERS PARA EL COMPONENTE FILTERBAR
    const handleFilterChange = (field, value) => setFilters(prev => ({ ...prev, [field]: value }));
    const clearFilter = (field) => setFilters(prev => ({ ...prev, [field]: '' }));
    const clearAllFilters = () => setFilters({ estado: '', motivo: '', riesgo: '', compania: '', patente: '' });

    const uniqueOptions = (field) => [...new Set(results.map(p => p[field]).filter(Boolean))].sort();
    const optionsEstados = ['VIGENTE', 'PENDIENTE DE EMISIÓN', 'PENDIENTE DE INSPECCIÓN', 'PROPUESTA INGRESADA', 'VENCIDA', 'ANULADA', 'NO INICIADA'];

    const handleNavigateToDetail = (item) => {
        const route = item.type === 'contrato' ? 'contratos' : 'polizas';
        navigate(`/clientes/${item.clienteId}/${route}/${item.id}`);
    };

    const handleSaveSuccess = (msg, updatedItem) => {
        setResults(prev => prev.map(p => p.id === updatedItem.id ? { ...p, ...updatedItem } : p));
        setShowEmissionModal(false); 
        setShowAnulacionModal(false);
        setShowEditContratoModal(false); 
        setShowAnulacionContratoModal(false);
    };

    const isProgressing = loading || syncing;

    return (
        <div className="min-h-screen bg-[#f3f4f8] font-sans text-left pb-16 w-full antialiased flex flex-col">
            
            {/* ENCABEZADO REUTILIZABLE DE LA PÁGINA */}
            <PageHeader 
                title="Buscador Global"
                subtitle="Consulta unificada de Pólizas Patrimoniales y Contratos ART"
                topBorderGradient="from-blue-600 via-indigo-600 to-purple-600"
            />

            {/* CONTENIDO PRINCIPAL DIRECTO EN EL FONDO */}
            <div className="w-full px-8 pt-8 max-w-[1300px] mx-auto space-y-6 flex-grow">
                
                {/* BUSCADOR INTELIGENTE (ANCHO max-w-5xl) */}
                <div className="bg-white p-6 rounded-3xl shadow-2xs border border-slate-200/80 max-w-5xl mx-auto text-left">
                    <BuscadorInteligente 
                        scope="global" 
                        onResult={handleSearchResult}
                        onLoading={setLoading}
                        placeholder="Buscar por Cliente, DNI, CUIT, Teléfono, Patente, Póliza o Contrato..."
                        autoFocus={true}
                    />
                </div>

                {/* ESTADO DE CARGA / RESULTADOS */}
                {isProgressing ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center animate-fade-in">
                        <div className="inline-block w-10 h-10 border-4 border-slate-200 border-t-indigo-600 rounded-full animate-spin"></div>
                        <p className="text-slate-500 mt-4 text-xs font-bold uppercase tracking-wider animate-pulse">
                            Sincronizando registros en tiempo real...
                        </p>
                    </div>
                ) : results.length > 0 ? (
                    <div className="animate-fade-in text-left space-y-5">
                        
                        {/* COMPONENTE REUTILIZABLE DE BARRA Y FILTROS DESPLEGABLES */}
                        <FilterBar 
                            totalResults={filteredResults.length}
                            filters={filters}
                            onFilterChange={handleFilterChange}
                            onClearFilter={clearFilter}
                            onClearAllFilters={clearAllFilters}
                            showFilters={showFilters}
                            setShowFilters={setShowFilters}
                            options={{
                                estados: optionsEstados,
                                companias: uniqueOptions('compania'),
                                riesgos: [...uniqueOptions('riesgo'), 'ART'].filter((v, i, a) => a.indexOf(v) === i).sort(),
                                motivos: [...new Set([...results.map(r => r.motivo), ...results.map(r => r.tipoOperacion)].filter(Boolean))].sort()
                            }}
                        />

                        {/* GRILLA DE TARJETAS (POLIZAS Y CONTRATOS) */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {filteredResults.map(item => (
                                item.type === 'contrato' ? (
                                    <CardContrato
                                        key={item.id}
                                        contrato={item}
                                        onNavigate={() => handleNavigateToDetail(item)}
                                        onView={() => handleViewDetailContrato(item)}
                                        onEdit={() => handleOpenEditContrato(item)}
                                        onAnular={() => handleOpenAnulacionContrato(item)}
                                        onDelete={() => handleDeleteClick(item)}
                                        onRehabilitar={() => handleRehabilitarClick(item)}
                                        isFullUser={isFullUser}
                                    />
                                ) : (
                                    <CardPoliza 
                                        key={item.id} 
                                        poliza={item} 
                                        onNavigate={() => handleNavigateToDetail(item)}
                                        onView={() => handleViewDetailPoliza(item)} 
                                        onEdit={() => handleOpenEmission(item)}
                                        onAnular={() => handleOpenAnulacionPoliza(item)}
                                        onDelete={() => handleDeleteClick(item)}
                                        onRehabilitar={() => handleRehabilitarClick(item)}
                                        isFullUser={isFullUser}
                                    />
                                )
                            ))}
                        </div>
                        
                        {/* ESTADO SIN COINCIDENCIAS LUEGO DE FILTRAR */}
                        {filteredResults.length === 0 && (
                            <div className="text-center py-12 bg-white rounded-3xl border border-dashed border-slate-300">
                                <p className="text-slate-500 font-bold text-sm">No hay registros que coincidan con los filtros aplicados.</p>
                                <button 
                                    onClick={clearAllFilters} 
                                    className="text-blue-600 text-xs font-bold mt-2 hover:underline cursor-pointer"
                                >
                                    Limpiar todos los filtros
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    searched ? (
                        <div className="flex flex-col items-center justify-center py-16 text-center animate-fade-in">
                            <div className="bg-white p-6 rounded-3xl shadow-2xs border border-slate-200/70 mb-4">
                                <svg className="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-black text-slate-800 mb-1">Sin resultados</h3>
                            <p className="text-slate-400 text-xs max-w-sm">No encontramos pólizas o contratos que coincidan con la búsqueda ingresada.</p>
                        </div>
                    ) : (
                        /* ESTADO INICIAL REFRESCADO "COMIENZA TU BÚSQUEDA" */
                        <div className="flex flex-col items-center justify-center py-12 text-center animate-fade-in max-w-xl mx-auto">
                            <div className="bg-white rounded-3xl border border-slate-200/80 p-8 shadow-sm space-y-4 w-full">
                                
                                <div className="p-3.5 bg-blue-50 border border-blue-100 rounded-2xl w-fit mx-auto text-blue-600">
                                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>

                                <div className="space-y-1.5">
                                    <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest bg-blue-50 px-3 py-1 rounded-full border border-blue-100 inline-block">
                                        Consulta Inteligente
                                    </span>
                                    <h3 className="text-xl font-black text-slate-900 tracking-tight">
                                        Comienza tu Búsqueda
                                    </h3>
                                    <p className="text-slate-400 text-xs font-medium max-w-md mx-auto leading-relaxed">
                                        Ingresa cualquier parámetro comercial para consultar la cartera unificada de la organización en tiempo real.
                                    </p>
                                </div>

                                <div className="pt-2 flex flex-wrap items-center justify-center gap-2">
                                    <span className="px-3 py-1 bg-slate-50 border border-slate-200/80 rounded-xl text-[11px] font-bold text-slate-600">
                                        👤 Nombre / Apellido
                                    </span>
                                    <span className="px-3 py-1 bg-slate-50 border border-slate-200/80 rounded-xl text-[11px] font-bold text-slate-600">
                                        💳 DNI / CUIT
                                    </span>
                                    <span className="px-3 py-1 bg-slate-50 border border-slate-200/80 rounded-xl text-[11px] font-bold text-slate-600">
                                        🚗 Patente
                                    </span>
                                    <span className="px-3 py-1 bg-slate-50 border border-slate-200/80 rounded-xl text-[11px] font-bold text-slate-600">
                                        📄 N° Póliza / Contrato
                                    </span>
                                </div>

                            </div>
                        </div>
                    )
                )}
                
            </div>

            {/* MODALES PÓLIZA */}
            <ModalEmision 
                show={showEmissionModal} 
                onClose={() => setShowEmissionModal(false)} 
                onSaveSuccess={handleSaveSuccess} 
                clientData={{ id: editingPoliza?.clienteId, nombre: editingPoliza?.clienteNombre || '' }} 
                initialData={editingPoliza} 
            />
            <ModalDetallePoliza 
                show={showDetailModal} 
                onClose={() => setShowDetailModal(false)} 
                poliza={viewingPoliza} 
            />
            <ModalAnulacion 
                show={showAnulacionModal} 
                onClose={() => setShowAnulacionModal(false)} 
                poliza={polizaToAnular} 
                clientId={polizaToAnular?.clienteId} 
                onSuccess={handleSaveSuccess} 
            />

            {/* MODALES CONTRATO (ART) */}
            <ModalEdicionContrato 
                show={showEditContratoModal} 
                onClose={() => setShowEditContratoModal(false)} 
                contrato={editingContrato} 
                clientData={{ id: editingContrato?.clienteId, nombre: editingContrato?.clienteNombre || '' }} 
                onSuccess={(msg) => handleSaveSuccess(msg, editingContrato)} 
            />
            <ModalDetalleContrato 
                show={showDetailContratoModal} 
                onClose={() => setShowDetailContratoModal(false)} 
                contrato={viewingContrato} 
            />
            <ModalAnulacionContratos 
                show={showAnulacionContratoModal} 
                onClose={() => setShowAnulacionContratoModal(false)} 
                contrato={contratoToAnular} 
                clientId={contratoToAnular?.clienteId} 
                onSuccess={(msg) => handleSaveSuccess(msg, contratoToAnular)} 
            />

            {/* AUTH MODAL PARA ELIMINAR Y REHABILITAR */}
            <AuthModal
                isOpen={authModalConfig.open}
                onClose={() => setAuthModalConfig({ open: false, type: '', item: null })}
                onConfirm={handleConfirmAuthAction}
                title={authModalConfig.type === 'DELETE' ? 'Eliminar Registro' : 'Rehabilitar Registro'}
                message={
                    authModalConfig.type === 'DELETE' 
                    ? '¿Estás seguro de eliminar este registro permanentemente? Se realizará una limpieza profunda de todos los datos vinculados.'
                    : '¿Estás seguro de que deseas rehabilitar este registro? Cambiará su estado a VIGENTE.'
                }
                requireAuth={true}
            />

            {/* FOOTER */}
            <footer className="mt-16 pt-6 border-t border-slate-200/80 text-center select-none shrink-0">
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                    &copy; {new Date().getFullYear()} IB BROKERS SYSTEM &bull; INTELLIGENT INSURANCE MANAGEMENT &bull; v2.5
                </p>
            </footer>

            <style>{`
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
                .animate-slide-down { animation: slideDown 0.25s ease-out forwards; }
                @keyframes slideDown { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default BuscadorPolizasPage;